/**
 * Credit Limit Check for B2B Cart
 *
 * Checks customer's available balance against cart total and controls checkout button
 */

/**
 * @typedef {Object} CustomerCreditData
 * @property {boolean} [staffAllocationBlocked]
 * @property {boolean} [loggedIn]
 * @property {boolean} [hasCompany]
 * @property {boolean} [onBlock]
 * @property {number|null} [overdueBalance]
 * @property {number|null} [availableBalance]
 * @property {number|null} [creditLimit]
 * @property {string} [currency]
 */

/** @type {Window & { customerCreditData?: CustomerCreditData }} */
const customWindow = window;

class CreditLimitCheck {
  constructor() {
    this.init();
  }

  /**
   * @returns {{
   *   checkoutButtons: NodeListOf<HTMLButtonElement>,
   *   creditLimitBanners: NodeListOf<HTMLElement>,
   *   accountBlockBanners: NodeListOf<HTMLElement>,
   *   expressPaymentButtons: NodeListOf<HTMLElement>
   * }}
   */
  getElements() {
    return {
      checkoutButtons: /** @type {NodeListOf<HTMLButtonElement>} */ (document.querySelectorAll('#checkout, [data-checkout-button]')),
      creditLimitBanners: document.querySelectorAll('[data-credit-limit-banner]'),
      accountBlockBanners: document.querySelectorAll('[data-account-block-banner]'),
      expressPaymentButtons: document.querySelectorAll('#express-payment-buttons')
    };
  }

  async init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.performCreditCheck());
    } else {
      this.performCreditCheck();
    }

    document.addEventListener('cart:update', () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.performCreditCheck();
        });
      });
    });

    this.observeCheckoutButton();
  }

  observeCheckoutButton() {
    let isCheckRunning = false;

    const observer = new MutationObserver((mutations) => {
      if (isCheckRunning) return;

      const hasRelevantChanges = mutations.some((mutation) =>
        mutation.type === 'childList' && mutation.addedNodes.length > 0
      );

      if (hasRelevantChanges) {
        const elements = this.getElements();
        if (elements.checkoutButtons.length > 0) {
          isCheckRunning = true;
          this.performCreditCheck().finally(() => {
            setTimeout(() => {
              isCheckRunning = false;
            }, 500);
          });
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  async performCreditCheck() {
    try {
      if (customWindow.customerCreditData?.staffAllocationBlocked) {
        this.disableCheckout();
        return;
      }

      if (!customWindow.customerCreditData || !customWindow.customerCreditData.loggedIn) {
        this.disableCheckout();
        return;
      }

      if (!customWindow.customerCreditData.hasCompany) {
        this.disableCheckout();
        return;
      }

      if (customWindow.customerCreditData.onBlock === true) {
        this.disableCheckout();
        return;
      }

      const overdueBalance = customWindow.customerCreditData.overdueBalance;
      if (overdueBalance != null && overdueBalance > 0) {
        this.disableCheckout();
        return;
      }

      const availableBalance = customWindow.customerCreditData.availableBalance;
      const creditLimit = customWindow.customerCreditData.creditLimit;
      const cartTotal = await this.getCartTotal();

      if (availableBalance == null || creditLimit == null || creditLimit === 0) {
        this.enableCheckout();
        return;
      }

      if (cartTotal > availableBalance) {
        this.disableCheckout(cartTotal, availableBalance);
      } else {
        this.enableCheckout();
      }
    } catch (error) {
      console.error('Credit limit check failed:', error);
      this.enableCheckout();
    }
  }

  async getCartTotal() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const cart = await response.json();
      return cart.total_price;
    } catch (error) {
      console.error('Error fetching cart total:', error);
      return 0;
    }
  }

  enableCheckout() {
    const elements = this.getElements();

    elements.checkoutButtons.forEach((btn) => {
      btn.disabled = false;
    });
    elements.creditLimitBanners.forEach((banner) => {
      banner.style.display = 'none';
    });
    elements.expressPaymentButtons.forEach((el) => {
      el.style.display = 'block';
    });
  }

  /**
   * @param {number|null} [cartTotal=null]
   * @param {number|null} [availableBalance=null]
   */
  disableCheckout(cartTotal = null, availableBalance = null) {
    const elements = this.getElements();

    elements.checkoutButtons.forEach((btn) => {
      btn.disabled = true;
    });

    elements.expressPaymentButtons.forEach((el) => {
      el.style.display = 'none';
    });

    if (cartTotal !== null && availableBalance !== null) {
      const formattedCartTotal = this.formatMoney(cartTotal);
      const formattedAvailableBalance = this.formatMoney(availableBalance);

      elements.creditLimitBanners.forEach((banner) => {
        const cartTotalEl = banner.querySelector('[data-credit-limit-cart-total]');
        const valueEl = banner.querySelector('[data-credit-limit-value]');
        if (cartTotalEl) cartTotalEl.textContent = formattedCartTotal;
        if (valueEl) valueEl.textContent = formattedAvailableBalance;
        banner.style.display = 'block';
      });
    }
  }

  /**
   * @param {number} amountInCents
   * @returns {string}
   */
  formatMoney(amountInCents) {
    const currency = customWindow.customerCreditData?.currency || 'EUR';
    /** @type {Record<string, string>} */
    const localeMap = {
      GBP: 'en-GB',
      EUR: 'fr-FR',
      USD: 'en-US'
    };
    const locale = localeMap[currency] || 'fr-FR';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(amountInCents / 100);
  }
}

new CreditLimitCheck();